// MARK:- Build a Bundle — FoxSell tiers in, dollar + percent savings out, bundle variant to /cart/add.js.

(function () {
  'use strict';

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.tiers = this.readTiers();
      this.bundleId = this.dataset.bundleId;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.grid = this.querySelector('[data-bab-grid]');
      this.summary = this.querySelector('[data-bab-summary]');
      this.itemsEl = this.querySelector('[data-bab-items]');
      this.priceEl = this.querySelector('[data-bab-price]');
      this.compareEl = this.querySelector('[data-bab-compare]');
      this.tierMessageEl = this.querySelector('[data-bab-tier-message]');
      this.tierFillEl = this.querySelector('[data-bab-tier-fill]');
      this.tierTicksEl = this.querySelector('[data-bab-tier-ticks]');
      this.atcBtn = this.querySelector('[data-bab-atc]');
      this.atcLabel = this.querySelector('[data-bab-atc-label]');
      this.ctaNote = this.querySelector('[data-bab-cta-note]');
      this.barStatus = this.querySelector('[data-bab-bar-status]');
      this.barPrice = this.querySelector('[data-bab-bar-price]');
      this.overlay = this.querySelector('[data-bab-overlay]');
      this.openBtn = this.querySelector('[data-bab-open]');
      this.closeBtn = this.querySelector('[data-bab-close]');
      this.addOnsEl = this.querySelector('[data-bab-addons]');
      this.cartAddUrl = this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || this.routeUrl('cart/add.js');
      this.cartUrl = this.dataset.cartUrl || window.Theme?.routes?.cart_url || this.routeUrl('cart');
      this.isSubmitting = false;
      this.isFlashingNote = false;

      this.atcDefaultLabel = this.atcLabel ? this.atcLabel.textContent.trim() : 'Add Bundle to Cart';

      // Product catalogue from the grid cards.
      this.products = new Map();
      this.querySelectorAll('[data-bab-card]').forEach((card) => {
        const key = card.dataset.cardKey || card.dataset.variantId;
        const variantId = card.dataset.variantId;
        if (!key || !variantId) return;
        this.products.set(key, {
          key,
          variantId,
          title: card.dataset.title,
          price: parseInt(card.dataset.price, 10) || 0,
          image: card.dataset.image,
          category: card.dataset.category || '',
          card,
        });
      });

      // Selection state: card key -> quantity. Insertion order is display order.
      this.quantities = new Map();
      this.addOns = new Set();
      this.addOnButtons = Array.from(this.querySelectorAll('[data-bab-addon-toggle]'));
      this.mediaQuery = window.matchMedia('(max-width: 989px)');
      this.handleMediaChange = () => this.updateSheetAccessibility();
      this.eventController?.abort();
      this.eventController = new AbortController();

      this.renderTicks();
      this.bindEvents();
      this.mediaQuery.addEventListener('change', this.handleMediaChange, { signal: this.eventController.signal });
      this.updateSheetAccessibility();
      this.render();
    }

    disconnectedCallback() {
      this.eventController?.abort();
      clearTimeout(this.flashTimer);
      clearTimeout(this.overlayTimer);
    }

    readTiers() {
      const el = this.querySelector('[data-bab-tiers]');
      if (!el) return [];
      try {
        const tiers = JSON.parse(el.textContent)
          .filter((t) => t.variantId && t.quantity > 0)
          .sort((a, b) => a.quantity - b.quantity);
        const uniqueTiers = new Map();
        tiers.forEach((tier) => {
          uniqueTiers.set(tier.quantity, tier);
        });
        return Array.from(uniqueTiers.values());
      } catch (error) {
        console.error('[build-a-bundle] Could not parse tiers', error);
        return [];
      }
    }

    bindEvents() {
      if (this.grid) {
        this.grid.addEventListener('click', (event) => {
          const card = event.target.closest('[data-bab-card]');
          if (!card) return;
          const key = card.dataset.cardKey || card.dataset.variantId;
          if (event.target.closest('[data-bab-add]') || event.target.closest('[data-bab-inc]')) {
            this.increment(key);
          } else if (event.target.closest('[data-bab-dec]')) {
            this.decrement(key);
          }
        }, { signal: this.eventController.signal });
      }

      this.itemsEl?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-bab-item-inc], [data-bab-item-dec]');
        if (!btn) return;
        const key = btn.closest('[data-bab-item]').dataset.cardKey;
        if (btn.hasAttribute('data-bab-item-inc')) {
          this.increment(key);
        } else {
          this.decrement(key);
        }
      }, { signal: this.eventController.signal });

      this.addOnButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const addOn = btn.closest('[data-bab-addon]');
          const id = addOn.dataset.variantId;
          const selected = !this.addOns.has(id);
          if (selected) {
            const max = this.addOnMaximum();
            if (max > 0 && this.addOns.size >= max) {
              this.flashNote('You can add up to ' + max + ' add-on' + (max === 1 ? '' : 's') + '.');
              return;
            }
            this.addOns.add(id);
          } else {
            this.addOns.delete(id);
          }
          addOn.classList.toggle('is-selected', selected);
          btn.setAttribute('aria-pressed', String(selected));
          btn.querySelector('[data-bab-addon-off]').hidden = selected;
          btn.querySelector('[data-bab-addon-on]').hidden = !selected;
          this.render();
        }, { signal: this.eventController.signal });
      });

      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', () => this.addToCart(), { signal: this.eventController.signal });
      }

      if (this.openBtn) this.openBtn.addEventListener('click', () => this.openSheet(), { signal: this.eventController.signal });
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeSheet(true), { signal: this.eventController.signal });
      if (this.overlay) this.overlay.addEventListener('click', () => this.closeSheet(true), { signal: this.eventController.signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeSheet(true);
          return;
        }
        if (event.key === 'Tab') this.trapSheetFocus(event);
      }, { signal: this.eventController.signal });
    }

    // --- State ---------------------------------------------------------------

    increment(key) {
      if (!this.products.has(key)) return;
      const maxTier = this.tiers[this.tiers.length - 1];
      if (maxTier && this.totalQuantity() >= maxTier.quantity) {
        this.flashNote('Your bundle is full at ' + maxTier.quantity + ' items.');
        return;
      }
      const isNew = !this.quantities.has(key);
      this.quantities.set(key, (this.quantities.get(key) || 0) + 1);
      this.render(isNew ? key : null);
    }

    decrement(key) {
      if (!this.quantities.has(key)) return;
      const next = this.quantities.get(key) - 1;
      if (next <= 0) {
        this.quantities.delete(key);
      } else {
        this.quantities.set(key, next);
      }
      this.render();
    }

    totalQuantity() {
      let total = 0;
      this.quantities.forEach((qty) => {
        total += qty;
      });
      return total;
    }

    subtotalCents() {
      let total = 0;
      this.quantities.forEach((qty, key) => {
        total += this.products.get(key).price * qty;
      });
      return total;
    }

    addOnsCents() {
      let total = 0;
      this.addOns.forEach((id) => {
        const el = this.querySelector('[data-bab-addon][data-variant-id="' + id + '"]');
        total += parseInt(el?.dataset.price, 10) || 0;
      });
      return total;
    }

    addOnMaximum() {
      const { current, next, exact } = this.tierState();
      const tierLimit = exact?.addOn?.maximum ?? current?.addOn?.maximum ?? next?.addOn?.maximum;
      const rootLimit = parseInt(this.addOnsEl?.dataset.addOnsMax, 10);
      const max = parseInt(tierLimit ?? rootLimit, 10);
      return Number.isFinite(max) ? max : 0;
    }

    addOnMinimum() {
      const { current, next, exact } = this.tierState();
      const tierLimit = exact?.addOn?.minimum ?? current?.addOn?.minimum ?? next?.addOn?.minimum;
      const rootLimit = parseInt(this.addOnsEl?.dataset.addOnsMin, 10);
      const min = parseInt(tierLimit ?? rootLimit, 10);
      return Number.isFinite(min) ? min : 0;
    }

    // Average unit price of the current selection, or the cheapest product when empty.
    // Used to project what the next tier would save.
    projectedUnitCents() {
      const count = this.totalQuantity();
      if (count > 0) return this.subtotalCents() / count;
      let min = Infinity;
      this.products.forEach((p) => {
        if (p.price > 0 && p.price < min) min = p.price;
      });
      return min === Infinity ? 0 : min;
    }

    // Discount in cents for a given tier applied to a given subtotal.
    discountCents(tier, subtotal) {
      if (!tier) return 0;
      if (tier.strategy === 'dynamic_pricing') {
        return Math.round((subtotal * tier.value) / 100);
      }
      // Fixed-price strategy: value is the bundle price in currency units.
      const fixed = Math.round(tier.value * 100);
      return Math.max(0, subtotal - fixed);
    }

    tierState() {
      const count = this.totalQuantity();
      let current = null;
      let next = this.tiers[0] || null;
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (count >= this.tiers[i].quantity) {
          current = this.tiers[i];
          next = this.tiers[i + 1] || null;
          break;
        }
      }
      const exact = this.tiers.find((t) => t.quantity === count) || null;
      return { count, current, next, exact };
    }

    percentLabel(tier, subtotal) {
      if (!tier) return '';
      if (tier.strategy === 'dynamic_pricing') return this.formatPercent(tier.value) + '%';
      if (!subtotal) return '';
      return this.formatPercent((this.discountCents(tier, subtotal) / subtotal) * 100) + '%';
    }

    // --- Render --------------------------------------------------------------

    render(newId) {
      const { count, current, next, exact } = this.tierState();
      const subtotal = this.subtotalCents();
      const savings = this.discountCents(current, subtotal);
      const addOns = this.addOnsCents();
      const total = subtotal - savings + addOns;

      this.priceEl.textContent = this.money(total);
      if (this.barPrice) this.barPrice.textContent = this.money(total);
      this.compareEl.hidden = savings <= 0;
      this.compareEl.textContent = savings > 0 ? this.money(subtotal + addOns) : '';

      this.renderTierMessage(count, current, next, subtotal, savings);
      this.renderProgress(count);
      this.renderCards();
      this.renderAddOns();
      this.renderItems(newId, count, current, next, subtotal);
      this.renderCta(count, exact, next, savings);
    }

    renderTierMessage(count, current, next, subtotal, savings) {
      const unit = this.projectedUnitCents();
      let html = '';
      let barText = '';

      if (!this.tiers.length) {
        html = '';
      } else if (!current && next) {
        const needed = next.quantity - count;
        const projected = this.discountCents(next, unit * next.quantity);
        const pct = this.percentLabel(next, unit * next.quantity);
        html =
          'Add <b>' + needed + ' more</b> to save <b>' + this.money(projected) + '</b>' + (pct ? ' (' + pct + ')' : '');
        barText = 'Add ' + needed + ' more for ' + pct + ' off';
      } else if (current && next) {
        const needed = next.quantity - count;
        const projected = this.discountCents(next, unit * next.quantity);
        const pct = this.percentLabel(next, unit * next.quantity);
        html =
          '<b>Saving ' + this.money(savings) + '</b> (' + this.percentLabel(current, subtotal) + ') — add ' +
          needed + ' more to save <b>' + this.money(projected) + '</b>' + (pct ? ' (' + pct + ')' : '');
        barText = 'Saving ' + this.money(savings) + ' · add ' + needed + ' more';
      } else if (current) {
        html = '<b>Saving ' + this.money(savings) + '</b> (' + this.percentLabel(current, subtotal) + ') — maximum savings unlocked';
        barText = 'Saving ' + this.money(savings) + ' · max savings';
      }

      this.tierMessageEl.innerHTML = html;
      this.tierMessageEl.classList.toggle('is-max', Boolean(current && !next));
      if (this.barStatus) this.barStatus.textContent = barText;
    }

    renderTicks() {
      if (!this.tierTicksEl || !this.tiers.length) return;
      const unit = this.projectedUnitCents();
      this.tierTicksEl.innerHTML = this.tiers
        .map((tier, i) => {
          const left = ((i + 1) / this.tiers.length) * 100;
          const pct = this.percentLabel(tier, unit * tier.quantity);
          return (
            '<div class="bab-tier__tick" data-tick="' + i + '" style="left:' + left.toFixed(1) + '%"><span>' +
            '<span class="bab-tier__tick-discount">' + pct + ' off</span>' +
            '<span class="bab-tier__tick-qty">' + tier.quantity + ' items</span></span></div>'
          );
        })
        .join('');
    }

    renderProgress(count) {
      if (!this.tiers.length) return;
      // Equal-width zones between tiers, starting from zero.
      let fill = 0;
      let prevQty = 0;
      const zone = 100 / this.tiers.length;
      for (let i = 0; i < this.tiers.length; i++) {
        const qty = this.tiers[i].quantity;
        if (count >= qty) {
          fill = zone * (i + 1);
        } else {
          fill = zone * i + ((count - prevQty) / (qty - prevQty)) * zone;
          break;
        }
        prevQty = qty;
      }
      this.tierFillEl.style.setProperty('--bab-fill', Math.min(100, Math.max(0, fill)).toFixed(1) + '%');
      this.tierTicksEl.querySelectorAll('[data-tick]').forEach((el, i) => {
        el.classList.toggle('is-active', count >= this.tiers[i].quantity);
      });
    }

    renderCards() {
      this.products.forEach((product, key) => {
        const qty = this.quantities.get(key) || 0;
        const qtyEl = product.card.querySelector('[data-bab-qty]');
        const countEl = product.card.querySelector('[data-bab-count]');
        product.card.classList.toggle('is-selected', qty > 0);
        if (qtyEl) qtyEl.hidden = qty === 0;
        if (countEl) countEl.textContent = qty;
      });
    }

    renderAddOns() {
      const max = this.addOnMaximum();
      this.addOnButtons.forEach((btn) => {
        const addOn = btn.closest('[data-bab-addon]');
        const isSelected = this.addOns.has(addOn.dataset.variantId);
        btn.disabled = max > 0 && this.addOns.size >= max && !isSelected;
      });
    }

    renderItems(newId, count, current, next, subtotal) {
      const fragment = document.createDocumentFragment();

      this.quantities.forEach((qty, key) => {
        const product = this.products.get(key);
        const row = document.createElement('div');
        row.className = 'bab-item' + (key === newId ? ' is-new' : '');
        row.setAttribute('data-bab-item', '');
        row.dataset.cardKey = key;
        row.innerHTML =
          '<div class="bab-item__thumb">' +
          (product.image ? '<img src="' + product.image + '" alt="" loading="lazy" width="36" height="36">' : '') +
          '</div>' +
          '<div class="bab-item__info"><div class="bab-item__title">' + this.escape(product.title) + '</div>' +
          '<div class="bab-item__price">' + this.money(product.price * qty) + '</div></div>' +
          '<div class="bab-item__qty">' +
          '<button type="button" class="bab-item__qty-btn" data-bab-item-dec aria-label="Decrease quantity">−</button>' +
          '<span class="bab-item__qty-count">' + qty + '</span>' +
          '<button type="button" class="bab-item__qty-btn" data-bab-item-inc aria-label="Increase quantity">+</button>' +
          '</div>';
        fragment.appendChild(row);
      });

      // Empty slots nudge toward the next tier.
      if (next) {
        const needed = next.quantity - count;
        const slots = Math.min(needed, 2);
        const unit = this.projectedUnitCents();
        const projected = this.discountCents(next, unit * next.quantity);
        for (let i = 0; i < slots; i++) {
          const row = document.createElement('div');
          row.className = 'bab-item bab-item--nudge';
          row.innerHTML =
            '<div class="bab-item__thumb bab-item__thumb--empty"></div>' +
            '<span class="bab-item__nudge">' +
            (i === 0 ? '<b>' + needed + ' more</b> to save ' + this.money(projected) + ' →' : '') +
            '</span>';
          fragment.appendChild(row);
        }
      } else if (current && count > 0) {
        const done = document.createElement('div');
        done.className = 'bab-items__max';
        done.textContent = '✓ Maximum savings applied';
        fragment.appendChild(done);
      }

      this.itemsEl.replaceChildren(fragment);
    }

    renderCta(count, exact, next, savings) {
      if (!this.atcBtn) return;
      if (this.isFlashingNote) return;
      // FoxSell bundle variants carry a fixed item count, so the cart button only
      // arms when the selection matches a tier exactly.
      const addOnMin = this.addOnMinimum();
      const needsAddOns = addOnMin > this.addOns.size;
      const addOnMax = this.addOnMaximum();
      const hasTooManyAddOns = addOnMax > 0 && this.addOns.size > addOnMax;
      const ready = Boolean(exact) && !needsAddOns && !hasTooManyAddOns;
      this.atcBtn.disabled = !ready;
      this.ctaNote.classList.toggle('is-hint', !ready);

      if (ready) {
        this.atcLabel.textContent = this.atcDefaultLabel;
        this.ctaNote.textContent = savings > 0 ? "You're saving " + this.money(savings) : '';
        return;
      }

      if (exact && needsAddOns) {
        const needed = addOnMin - this.addOns.size;
        this.atcLabel.textContent = 'Add ' + needed + ' add-on' + (needed === 1 ? '' : 's');
        this.ctaNote.textContent = 'This bundle needs ' + addOnMin + ' add-on' + (addOnMin === 1 ? '' : 's') + '.';
        return;
      }

      if (exact && hasTooManyAddOns) {
        const extra = this.addOns.size - addOnMax;
        this.atcLabel.textContent = 'Remove ' + extra + ' add-on' + (extra === 1 ? '' : 's');
        this.ctaNote.textContent = 'This bundle allows up to ' + addOnMax + ' add-on' + (addOnMax === 1 ? '' : 's') + '.';
        return;
      }

      if (next) {
        const needed = next.quantity - count;
        this.atcLabel.textContent = 'Add ' + needed + ' more';
        this.ctaNote.textContent = 'Bundles come in ' + this.tierSizes() + ' items.';
      } else {
        this.atcLabel.textContent = this.atcDefaultLabel;
        this.ctaNote.textContent = '';
      }
    }

    tierSizes() {
      const sizes = this.tiers.map((t) => t.quantity);
      if (sizes.length <= 1) return sizes.join('');
      return sizes.slice(0, -1).join(', ') + ' or ' + sizes[sizes.length - 1];
    }

    flashNote(text) {
      if (!this.ctaNote) return;
      this.isFlashingNote = true;
      this.ctaNote.textContent = text;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        this.isFlashingNote = false;
        this.render();
      }, 2000);
    }

    // --- Mobile sheet --------------------------------------------------------

    openSheet() {
      if (!this.summary) return;
      this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : this.openBtn;
      clearTimeout(this.overlayTimer);
      this.overlay.hidden = false;
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-visible');
        this.summary.classList.add('is-open');
        this.updateSheetAccessibility();
        this.firstFocusableInSheet()?.focus();
      });
      document.documentElement.classList.add('bab-sheet-open');
      this.openBtn?.setAttribute('aria-expanded', 'true');
    }

    closeSheet(returnFocus = false) {
      if (!this.summary || !this.summary.classList.contains('is-open')) return;
      this.summary.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      document.documentElement.classList.remove('bab-sheet-open');
      this.openBtn?.setAttribute('aria-expanded', 'false');
      this.updateSheetAccessibility();
      if (returnFocus) {
        (this.lastFocusedElement || this.openBtn)?.focus();
      }
      clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => {
        this.overlay.hidden = true;
      }, 250);
    }

    updateSheetAccessibility() {
      if (!this.summary) return;
      const shouldHide = this.mediaQuery?.matches && !this.summary.classList.contains('is-open');
      if (shouldHide) {
        this.summary.setAttribute('aria-hidden', 'true');
        this.summary.inert = true;
      } else {
        this.summary.removeAttribute('aria-hidden');
        this.summary.inert = false;
      }
    }

    firstFocusableInSheet() {
      return this.sheetFocusableElements()[0] || this.summary;
    }

    sheetFocusableElements() {
      if (!this.summary) return [];
      return Array.from(
        this.summary.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el instanceof HTMLElement && el.getClientRects().length > 0);
    }

    trapSheetFocus(event) {
      if (!this.mediaQuery?.matches || !this.summary?.classList.contains('is-open')) return;
      const focusable = this.sheetFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.summary.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // --- Cart ----------------------------------------------------------------

    async addToCart() {
      if (this.isSubmitting) return;
      const { exact } = this.tierState();
      if (!exact || !this.bundleId) return;
      if (this.addOnMinimum() > this.addOns.size) {
        this.render();
        return;
      }
      const addOnMax = this.addOnMaximum();
      if (addOnMax > 0 && this.addOns.size > addOnMax) {
        this.render();
        return;
      }

      const items = [];
      this.quantities.forEach((qty, key) => {
        const product = this.products.get(key);
        items.push({
          variantId: product.variantId,
          quantity: qty,
          // Must match the category name in the FoxSell app config exactly.
          category: product.category,
          type: 'product',
          properties: {},
        });
      });

      // FoxSell expects add-ons as type "addOns" with an empty category; the
      // price override (free vs paid) is applied server-side.
      this.addOns.forEach((id) => {
        items.push({
          variantId: id,
          quantity: 1,
          category: '',
          type: 'addOns',
          properties: {},
        });
      });

      const identifier = this.bundleId + '_' + Date.now();
      const payload = {
        items: [
          {
            id: exact.variantId,
            quantity: 1,
            properties: {
              '__foxsell:dynamic_add_on_bundle_id': identifier,
              '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(items),
            },
          },
        ],
      };

      this.isSubmitting = true;
      this.atcBtn.setAttribute('aria-busy', 'true');
      this.atcBtn.setAttribute('aria-disabled', 'true');
      this.atcBtn.disabled = true;
      let didRedirect = false;

      try {
        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Status ' + response.status);
        }

        didRedirect = true;
        window.location.href = this.cartUrl;
      } catch (error) {
        console.error('[build-a-bundle]', error);
        this.ctaNote.classList.add('is-hint');
        this.ctaNote.textContent = 'Could not add the bundle. ' + (error.message || 'Please try again.');
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              source: 'build-a-bundle',
              variantId: exact.variantId,
              message: 'Error adding bundle to cart',
              errors: error.message,
            },
          })
        );
      } finally {
        if (didRedirect) return;
        this.isSubmitting = false;
        this.atcBtn.removeAttribute('aria-busy');
        this.atcBtn.removeAttribute('aria-disabled');
        const addOnMax = this.addOnMaximum();
        this.atcBtn.disabled =
          !this.tierState().exact || this.addOnMinimum() > this.addOns.size || (addOnMax > 0 && this.addOns.size > addOnMax);
      }
    }

    // --- Utils ---------------------------------------------------------------

    money(cents) {
      const format = this.moneyFormat;
      const value = Math.round(cents);
      const withDecimals = (v, thousands, decimal) => {
        const fixed = (v / 100).toFixed(2);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };
      const noDecimals = (v, thousands) =>
        String(Math.round(v / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const key = match ? match[1] : 'amount';
      let formatted;
      switch (key) {
        case 'amount_no_decimals':
          formatted = noDecimals(value, ',');
          break;
        case 'amount_with_comma_separator':
          formatted = withDecimals(value, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = noDecimals(value, '.');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = withDecimals(value, "'", '.');
          break;
        default:
          formatted = withDecimals(value, ',', '.');
      }
      return format.replace(/\{\{\s*\w+\s*\}\}/, formatted).replace(/<[^>]*>/g, '');
    }

    formatPercent(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '0';
      return number.toFixed(1).replace(/\.0$/, '');
    }

    routeUrl(path) {
      const root = window.Shopify?.routes?.root || '/';
      return root + path;
    }

    escape(text) {
      const div = document.createElement('div');
      div.textContent = text || '';
      return div.innerHTML;
    }
  }

  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', BundleBuilder);
  }
})();
